/**
 * The dock sheet, and a member's own water.
 *
 * A dockmaster works from one page: every slip, what is in it today,
 * who is arriving and who is leaving — and the empty ones, because an
 * empty slip on a Friday in July is the thing they are looking for.
 *
 * The column this page exists for is the one marked "away". A seasonal
 * holder has gone down the river for two weeks and told the club it
 * may sell the nights; those nights are the largest thing a small
 * marina leaves on the table, and they are invisible unless a screen
 * puts them in front of somebody.
 */

import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { clubToday, dayLabel, shiftDate } from '../lib/sheet.ts';
import { money, date as fmtDate } from '../lib/format.ts';

interface SheetRow {
  dock_name: string;
  slip_id: string;
  slip_code: string;
  length_feet: string | null;
  power: string;
  occupancy_id: string | null;
  kind: 'lease' | 'transient' | 'held' | 'out_of_service' | null;
  boat_name: string | null;
  hailing_port: string | null;
  payer_name: string | null;
  first_night: string | null;
  last_night: string | null;
  stay_id: string | null;
  stay_status: string | null;
  arriving: boolean;
  departing: boolean;
  note: string | null;
}

interface AvailableSlip {
  slip_id: string;
  code: string;
  dock_name: string;
  length_feet: string;
  beam_feet: string;
  depth_feet: string | null;
  power: string;
  is_relet: boolean;
}

interface Stay {
  stay_id: string;
  status: string;
  payer_kind: string;
  first_night: string;
  last_night: string;
  nights: number;
  slip_code: string;
  boat_name: string;
  hailing_port: string | null;
  loa_feet: string | null;
  billable_feet: string | null;
  payer_name: string | null;
  payer_reference: string | null;
  persons_aboard: number | null;
  last_port: string | null;
  next_port: string | null;
  power_used: string;
  unbilled: boolean;
  quoted_amount_minor: number | null;
}


/**
 * Anchored at noon UTC on purpose: adding a day to a midnight date rolls
 * backwards across a daylight saving boundary, and a sheet that skips a
 * Sunday every spring is a bug nobody reproduces on a Tuesday.
 */
const POWER_LABEL: Record<string, string> = {
  none: '—', '15a': '15A', '30a': '30A', '50a': '50A',
  '30a_x2': '2 × 30A', '50a_x2': '2 × 50A',
};

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

// =====================================================================

export function DockSheetPage() {
  const { atLeast, club } = useSession();
  const canEdit = atLeast('staff');
  const zone = club?.timezone;

  // The club's today. It was UTC's, which on the Rock River is
  // tomorrow's sheet from seven in the evening.
  const [date, setDate] = useState(() => clubToday(zone));
  const [note, setNote] = useState<string | null>(null);
  const [arriving, setArriving] = useState<AvailableSlip | null>(null);

  const sheet = useApi<{ rows: SheetRow[] }>(`/v1/boating/dock-sheet?date=${date}`, [date]);
  const free = useApi<{ slips: AvailableSlip[] }>(
    `/v1/boating/availability?from=${date}&to=${date}`, [date]);

  const rows = sheet.data?.rows ?? [];
  const open = free.data?.slips ?? [];

  const occupied = rows.filter((r) => r.occupancy_id !== null);
  const transients = occupied.filter((r) => r.kind === 'transient');
  const relets = open.filter((s) => s.is_relet);
  const outOfService = occupied.filter((r) => r.kind === 'out_of_service');

  const byDock = rows.reduce<Record<string, SheetRow[]>>((acc, r) => {
    (acc[r.dock_name] ??= []).push(r);
    return acc;
  }, {});

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Dock Sheet</h1>
          <div className="muted small">
            <strong>{dayLabel(date, zone)}</strong> · who is on the water, who is coming, and what
            is empty
          </div>
        </div>
        <div className="spacer" />
        <Actions>
          <button className="secondary small" onClick={() => setDate(shiftDate(date, -1))}>‹</button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <button className="secondary small" onClick={() => setDate(shiftDate(date, 1))}>›</button>
          <button className="secondary small" onClick={() => setDate(clubToday(zone))}>Today</button>
        </Actions>
      </div>

      <ErrorBanner error={sheet.error} />
      <ErrorBanner error={free.error} />

      {note && (
        <Card><Status tone="good">{note}</Status></Card>
      )}

      <div className="tiles">
        <Tile label="Slips occupied" value={`${occupied.length} of ${rows.length}`} />
        <Tile label="Transients alongside" value={transients.length} />
        <Tile
          label="Free tonight"
          value={open.length}
          note={relets.length > 0
            ? `${relets.length} because a holder is away`
            : undefined}
          tone={open.length === 0 ? 'warning' : undefined}
        />
        <Tile
          label="Out of service"
          value={outOfService.length}
          tone={outOfService.length > 0 ? 'warning' : undefined}
        />
      </div>

      {relets.length > 0 && (
        <Card title="Nights the Club Can Sell">
          <div className="muted small" style={{ marginBottom: 10 }}>
            These slips are empty because their season holder is away and said the club
            may re-let them. They are already paid for once.
          </div>
          <table className="data">
            <thead>
              <tr><th>Slip</th><th>Dock</th><th>Length</th><th>Power</th><th /></tr>
            </thead>
            <tbody>
              {relets.map((s) => (
                <tr key={s.slip_id}>
                  <td className="mono">{s.code}</td>
                  <td>{s.dock_name}</td>
                  <td>{s.length_feet} ft</td>
                  <td>{POWER_LABEL[s.power] ?? s.power}</td>
                  <td style={{ textAlign: 'right' }}>
                    {canEdit && (
                      <button className="small" onClick={() => setArriving(s)}>
                        Log an arrival
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {arriving && (
        <ArrivalForm
          slip={arriving}
          date={date}
          onDone={(m) => {
            setArriving(null);
            setNote(m);
            sheet.reload();
            free.reload();
          }}
          onCancel={() => setArriving(null)}
        />
      )}

      {sheet.loading ? (
        <Loading rows={10} />
      ) : rows.length === 0 ? (
        <Card>
          <Empty>
            This club has no slips yet. A dock, its slips and its rate card are set up
            under Marina administration.
          </Empty>
        </Card>
      ) : (
        Object.entries(byDock).map(([dock, dockRows]) => (
          <Card key={dock} title={dock} flush>
            <table className="data">
              <thead>
                <tr>
                  <th>Slip</th>
                  <th>Length</th>
                  <th>Power</th>
                  <th>Boat</th>
                  <th>From</th>
                  <th>Held by</th>
                  <th>Nights</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dockRows.map((r) => (
                  <tr key={r.slip_id}>
                    <td className="mono">{r.slip_code}</td>
                    <td>{r.length_feet ? `${r.length_feet} ft` : '—'}</td>
                    <td className="small">{POWER_LABEL[r.power] ?? r.power}</td>
                    <td>
                      {r.boat_name ?? (
                        <span className="muted small">
                          {r.kind === 'out_of_service' ? 'out of service' : 'empty'}
                        </span>
                      )}
                      {r.kind === 'transient' && (
                        <span className="pill" style={{ marginLeft: 6 }}>transient</span>
                      )}
                      {r.kind === 'held' && (
                        <span className="pill" style={{ marginLeft: 6 }}>held</span>
                      )}
                    </td>
                    <td className="small muted">{r.hailing_port ?? ''}</td>
                    <td className="small">{r.payer_name ?? ''}</td>
                    <td className="small">
                      {r.first_night && r.last_night
                        ? `${fmtDate(r.first_night)} – ${fmtDate(r.last_night)}`
                        : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {r.arriving && <span className="pill">arriving</span>}
                      {r.departing && <span className="pill">departing</span>}
                      {r.note && !r.boat_name && (
                        <span className="muted small" title={r.note}>
                          {r.note.length > 40 ? r.note.slice(0, 40) + '…' : r.note}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))
      )}
    </>
  );
}

// =====================================================================

function ArrivalForm({
  slip, date, onDone, onCancel,
}: {
  slip: AvailableSlip;
  date: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState({
    boat_name: '',
    hailing_port: '',
    loa_feet: '',
    beam_feet: '',
    draft_feet: '',
    visitor_name: '',
    visitor_phone: '',
    last_port: '',
    next_port: '',
    persons_aboard: '2',
    power: 'none',
    first_night: date,
    last_night: date,
  });

  const log = useMutation(async () => {
    // The boat first, because a stay with no boat is not a stay. A
    // stranger's boat is created here from what was shouted across the
    // water; a member's is already on file.
    const boat = await api<{ id: string }>('/v1/boating/boats', {
      method: 'POST',
      body: {
        name: f.boat_name,
        hailing_port: f.hailing_port || null,
        owner_display_name: f.visitor_name,
        hull_type: 'power',
        loa_feet: f.loa_feet ? Number(f.loa_feet) : null,
        beam_feet: f.beam_feet ? Number(f.beam_feet) : null,
        draft_feet: f.draft_feet ? Number(f.draft_feet) : null,
      },
    });

    await api('/v1/boating/transient-log', {
      method: 'POST',
      body: {
        slip_id: slip.slip_id,
        boat_id: boat.id,
        first_night: f.first_night,
        last_night: f.last_night,
        payer_kind: 'visitor',
        visitor_name: f.visitor_name,
        visitor_phone: f.visitor_phone || null,
        last_port: f.last_port || null,
        next_port: f.next_port || null,
        persons_aboard: f.persons_aboard ? Number(f.persons_aboard) : null,
        power: f.power,
        alongside: true,
      },
    });

    onDone(`${f.boat_name} is logged into ${slip.code}.`);
  });

  return (
    <Card title={`Alongside ${slip.code}`}>
      <ErrorBanner error={log.error} />
      <div className="muted small" style={{ marginBottom: 10 }}>
        The harbour book: what she is, where she is in from, where she is bound and how
        many are aboard. The last three are what somebody ashore rings to ask.
      </div>
      <form onSubmit={(e) => { e.preventDefault(); log.run(); }}>
        <div className="grid-2">
          <label className="field">
            <span>Boat name</span>
            <input value={f.boat_name} required
              onChange={(e) => setF({ ...f, boat_name: e.target.value })} />
          </label>
          <label className="field">
            <span>Hailing port</span>
            <input value={f.hailing_port}
              onChange={(e) => setF({ ...f, hailing_port: e.target.value })} />
          </label>
          <label className="field">
            <span>Length overall (ft)</span>
            <input type="number" step="0.5" value={f.loa_feet}
              onChange={(e) => setF({ ...f, loa_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Beam (ft)</span>
            <input type="number" step="0.5" value={f.beam_feet}
              onChange={(e) => setF({ ...f, beam_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Draft (ft)</span>
            <input type="number" step="0.5" value={f.draft_feet}
              onChange={(e) => setF({ ...f, draft_feet: e.target.value })} />
          </label>
          <label className="field">
            <span>Souls aboard</span>
            <input type="number" min="0" value={f.persons_aboard}
              onChange={(e) => setF({ ...f, persons_aboard: e.target.value })} />
          </label>
          <label className="field">
            <span>In the name of</span>
            <input value={f.visitor_name} required
              onChange={(e) => setF({ ...f, visitor_name: e.target.value })} />
          </label>
          <label className="field">
            <span>Telephone</span>
            <input value={f.visitor_phone}
              onChange={(e) => setF({ ...f, visitor_phone: e.target.value })} />
          </label>
          <label className="field">
            <span>In from</span>
            <input value={f.last_port}
              onChange={(e) => setF({ ...f, last_port: e.target.value })} />
          </label>
          <label className="field">
            <span>Bound for</span>
            <input value={f.next_port}
              onChange={(e) => setF({ ...f, next_port: e.target.value })} />
          </label>
          <label className="field">
            <span>First night</span>
            <input type="date" value={f.first_night}
              onChange={(e) => setF({ ...f, first_night: e.target.value })} />
          </label>
          <label className="field">
            <span>Last night aboard</span>
            <input type="date" value={f.last_night}
              onChange={(e) => setF({ ...f, last_night: e.target.value })} />
          </label>
          <label className="field">
            <span>Shore power</span>
            <select value={f.power} onChange={(e) => setF({ ...f, power: e.target.value })}>
              <option value="none">None</option>
              <option value="30a">30A</option>
              <option value="50a">50A</option>
              <option value="50a_x2">2 × 50A</option>
            </select>
          </label>
        </div>
        <div className="muted small" style={{ marginTop: 6 }}>
          The last night aboard, not the departure date. A boat in on Friday and away
          Saturday morning is one night.
        </div>
        <Actions>
          <button type="submit" disabled={log.busy}>
            {log.busy ? 'Logging…' : 'Log her in'}
          </button>
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
        </Actions>
      </form>
    </Card>
  );
}

// =====================================================================

/** The harbour book. */
export function TransientLogPage() {
  const { atLeast, currency } = useSession();
  const canEdit = atLeast('staff');

  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const log = useApi<{ stays: Stay[] }>(
    `/v1/boating/transient-log${unbilledOnly ? '?unbilled=true' : ''}`, [unbilledOnly]);

  const depart = useMutation(async (id: string) => {
    const r = await api<{ nights: number; extended: boolean; warning: string | null }>(
      `/v1/boating/transient-log/${id}/depart`, { method: 'POST', body: {} });
    setNote(r.warning ?? `Logged out — ${r.nights} night${r.nights === 1 ? '' : 's'}.`);
    log.reload();
  });

  const post = useMutation(async (id: string) => {
    const r = await api<{
      fees: { fee: string; amount_minor: number; not_posted_reason: string | null }[];
      settled_at_the_counter: boolean;
    }>(`/v1/boating/transient-log/${id}/post`, { method: 'POST', body: {} });

    const posted = r.fees.filter((x) => !x.not_posted_reason);
    const total = posted.reduce((s, x) => s + Number(x.amount_minor), 0);
    setNote(
      posted.length === 0
        ? r.fees.map((x) => x.not_posted_reason).join(' ')
        : `${money(total, currency)} — ${posted.map((x) => x.fee.replace(/_/g, ' ')).join(', ')}` +
          (r.settled_at_the_counter
            ? '. Taken at the counter; it is in undeposited funds until the drawer is banked.'
            : '. Posted to the account.'),
    );
    log.reload();
  });

  const stays = log.data?.stays ?? [];
  const unbilled = stays.filter((s) => s.unbilled);

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Transient Log</h1>
          <div className="muted small">
            What tied up, where she was in from, who was aboard, and who pays
          </div>
        </div>
        <div className="spacer" />
        <Actions>
          <button
            className={unbilledOnly ? 'gold small' : 'secondary small'}
            onClick={() => setUnbilledOnly(!unbilledOnly)}
          >
            {unbilledOnly ? 'Showing unbilled' : `Unbilled (${unbilled.length})`}
          </button>
        </Actions>
      </div>

      <ErrorBanner error={log.error} />
      <ErrorBanner error={depart.error} />
      <ErrorBanner error={post.error} />

      {note && <Card><Status tone="good">{note}</Status></Card>}

      {log.loading ? (
        <Loading rows={8} />
      ) : stays.length === 0 ? (
        <Card><Empty>Nothing in the book yet.</Empty></Card>
      ) : (
        <Card flush>
          <table className="data">
            <thead>
              <tr>
                <th>Boat</th>
                <th>In from</th>
                <th>Slip</th>
                <th>Nights</th>
                <th>Aboard</th>
                <th>Pays</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stays.map((s) => (
                <tr key={s.stay_id}>
                  <td>
                    <strong>{s.boat_name}</strong>
                    {s.loa_feet && <span className="muted small"> · {s.loa_feet} ft</span>}
                    {s.billable_feet && s.billable_feet !== s.loa_feet && (
                      <span className="muted small"> · billed {s.billable_feet} ft</span>
                    )}
                  </td>
                  <td className="small muted">{s.hailing_port ?? s.last_port ?? ''}</td>
                  <td className="mono">{s.slip_code}</td>
                  <td>
                    {s.nights}
                    <div className="muted small">
                      {fmtDate(s.first_night)} – {fmtDate(s.last_night)}
                    </div>
                  </td>
                  <td>{s.persons_aboard ?? '—'}</td>
                  <td className="small">
                    {s.payer_name ?? '—'}
                    <div className="muted small">
                      {s.payer_kind === 'visitor' ? 'at the counter' : 'to the account'}
                    </div>
                  </td>
                  <td>
                    <span className="pill">{s.status}</span>
                    {s.unbilled && <span className="pill" style={{ marginLeft: 4 }}>unbilled</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canEdit && s.status === 'alongside' && (
                      <button className="small secondary" disabled={depart.busy}
                        onClick={() => depart.run(s.stay_id)}>
                        Log her out
                      </button>
                    )}
                    {canEdit && s.unbilled && (
                      <button className="small" disabled={post.busy}
                        onClick={() => post.run(s.stay_id)}>
                        Bill it
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// =====================================================================

/** A member's own water. */
export function MyBoatingPage() {
  const { currency } = useSession();
  const mine = useApi<{
    leases: {
      lease_id: string; slip_code: string; dock_name: string; starts_on: string;
      ends_on: string; boat_name: string | null; status: string;
      relettable_nights: number; insurance_lapsed: boolean;
      season_amount_minor: number | null;
    }[];
    boats: {
      boat_id: string; name: string; loa_feet: string | null; hull_type: string;
      insurance_expires_on: string | null; insurance_lapsed: boolean;
    }[];
    rentals: {
      booking_id: string; boat_name: string; starts_at: string; ends_at: string;
      status: string; operator_cleared: boolean | null; operator_note: string | null;
    }[];
    absences: {
      id: string; slip_code: string; starts_on: string; ends_on: string;
      may_relet: boolean; note: string | null;
    }[];
  }>('/v1/boating/mine');

  const [away, setAway] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const d = mine.data;

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>My Boating</h1>
          <div className="muted small">Your slip, your boats, and what you have booked</div>
        </div>
      </div>

      <ErrorBanner error={mine.error} />
      {note && <Card><Status tone="good">{note}</Status></Card>}

      {mine.loading ? (
        <Loading rows={6} />
      ) : (
        <>
          <Card title="Your Slip">
            {(d?.leases ?? []).length === 0 ? (
              <Empty>
                You do not hold a slip. The dockmaster keeps the waiting list — ask to be
                put on it, and the day you asked is the day you are ordered by.
              </Empty>
            ) : (
              d!.leases.map((l) => (
                <div key={l.lease_id} style={{ marginBottom: 12 }}>
                  <div className="row">
                    <div>
                      <strong className="mono">{l.slip_code}</strong>{' '}
                      <span className="muted">{l.dock_name}</span>
                      <div className="muted small">
                        {fmtDate(l.starts_on)} – {fmtDate(l.ends_on)}
                        {l.boat_name && ` · ${l.boat_name}`}
                        {l.season_amount_minor && ` · ${money(l.season_amount_minor, currency)} for the season`}
                      </div>
                    </div>
                    <div className="spacer" />
                    <button className="small secondary" onClick={() => setAway(l.lease_id)}>
                      I'll be away
                    </button>
                  </div>
                  {l.insurance_lapsed && (
                    <Status tone="warning">
                      The insurance certificate on file for {l.boat_name} has expired. The
                      dockmaster will need a current one.
                    </Status>
                  )}
                  {away === l.lease_id && (
                    <AbsenceForm
                      leaseId={l.lease_id}
                      onDone={(m) => { setAway(null); setNote(m); mine.reload(); }}
                      onCancel={() => setAway(null)}
                    />
                  )}
                </div>
              ))
            )}
          </Card>

          {(d?.absences ?? []).length > 0 && (
            <Card title="When You're Away">
              <table className="data">
                <thead>
                  <tr><th>Slip</th><th>From</th><th>Until</th><th>The club may re-let</th><th>Note</th></tr>
                </thead>
                <tbody>
                  {d!.absences.map((a) => (
                    <tr key={a.id}>
                      <td className="mono">{a.slip_code}</td>
                      <td>{fmtDate(a.starts_on)}</td>
                      <td>{fmtDate(a.ends_on)}</td>
                      <td>{a.may_relet ? 'Yes' : 'No'}</td>
                      <td className="small muted">{a.note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <Card title="Your Boats">
            {(d?.boats ?? []).length === 0 ? (
              <Empty>No boats on file.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr><th>Name</th><th>Type</th><th>Length</th><th>Insurance</th></tr>
                </thead>
                <tbody>
                  {d!.boats.map((b) => (
                    <tr key={b.boat_id}>
                      <td><strong>{b.name}</strong></td>
                      <td className="small">{b.hull_type}</td>
                      <td>{b.loa_feet ? `${b.loa_feet} ft` : '—'}</td>
                      <td className="small">
                        {b.insurance_expires_on
                          ? <span className={b.insurance_lapsed ? 'status' : ''}>
                              {b.insurance_lapsed ? 'Expired ' : 'Until '}
                              {fmtDate(b.insurance_expires_on)}
                            </span>
                          : <span className="muted">nothing on file</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Booked">
            {(d?.rentals ?? []).length === 0 ? (
              <Empty>Nothing booked from the club fleet.</Empty>
            ) : (
              <table className="data">
                <thead><tr><th>Boat</th><th>When</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {d!.rentals.map((r) => (
                    <tr key={r.booking_id}>
                      <td>{r.boat_name}</td>
                      <td className="small">{new Date(r.starts_at).toLocaleString()}</td>
                      <td><span className="pill">{r.status}</span></td>
                      <td className="small muted">{r.operator_note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function AbsenceForm({
  leaseId, onDone, onCancel,
}: {
  leaseId: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const { club } = useSession();
  const [f, setF] = useState(() => {
    const day = clubToday(club?.timezone);
    return { starts_on: day, ends_on: day, may_relet: true, note: '' };
  });

  const report = useMutation(async () => {
    const r = await api<{ nights: number; sellable: boolean }>(
      `/v1/boating/leases/${leaseId}/absence`,
      { method: 'POST', body: { ...f, note: f.note || null } });
    onDone(
      r.sellable
        ? `Noted — ${r.nights} night${r.nights === 1 ? '' : 's'}. The dockmaster may let ` +
          'the slip while you are gone.'
        : `Noted — ${r.nights} night${r.nights === 1 ? '' : 's'}. Your slip will be kept empty.`,
    );
  });

  return (
    <div style={{ marginTop: 10 }}>
      <ErrorBanner error={report.error} />
      <form onSubmit={(e) => { e.preventDefault(); report.run(); }}>
        <div className="grid-2">
          <label className="field">
            <span>Away from</span>
            <input type="date" value={f.starts_on}
              onChange={(e) => setF({ ...f, starts_on: e.target.value })} />
          </label>
          <label className="field">
            <span>Back by</span>
            <input type="date" value={f.ends_on}
              onChange={(e) => setF({ ...f, ends_on: e.target.value })} />
          </label>
        </div>
        <label className="field">
          <span>Where you're going (optional)</span>
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </label>
        <label className="field" style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={f.may_relet}
            onChange={(e) => setF({ ...f, may_relet: e.target.checked })} />
          <span>The club may let my slip to a visiting boat while I'm gone</span>
        </label>
        <div className="muted small">
          Either way it helps to know: a slip nobody can reach the owner of is a slip the
          club cannot move in a storm.
        </div>
        <Actions>
          <button type="submit" disabled={report.busy}>
            {report.busy ? 'Saving…' : 'Tell the dockmaster'}
          </button>
          <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
        </Actions>
      </form>
    </div>
  );
}
